import React from 'react';
import { useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';

interface SidebarProps {
  isCollapsed?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  onToggleCollapse?: () => void;
  userRole?: 'admin' | 'partner'; // CRITICAL FIX: Add userRole prop to filter menu items
}

const Sidebar: React.FC<SidebarProps> = ({ 
  isCollapsed, 
  isOpen, 
  onClose, 
  onToggleCollapse,
  userRole = 'admin' // CRITICAL FIX: Default to 'admin' for backward compatibility
}) => {
  const location = useLocation();

  // CRITICAL FIX: Define ALL navigation items with role access control
  const allNavigationItems = [
    {
      label: 'Overview',
      path: '/executive-overview',
      icon: 'BarChart3',
      description: 'Executive command center and strategic KPIs',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'Users',
      path: '/user-management',
      icon: 'Users',
      description: 'User management and administration dashboard',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'QR Codes',
      path: '/qr-code-management',
      icon: 'QrCode',
      description: 'QR code activation management and tracking',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'Offers',
      path: '/offer-management',
      icon: 'Tag',
      description: 'Offer performance monitoring and optimization',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'Partners',
      path: '/partner-management-dashboard',
      icon: 'Handshake',
      description: 'Partner management and business administration',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'Acquisition',
      path: '/partner-acquisition',
      icon: 'UserPlus',
      description: 'Partner acquisition and onboarding workflows',
      allowedRoles: ['admin'] // Only admins can see this
    },
    {
      label: 'Redemptions',
      path: '/redemption-analytics',
      icon: 'CreditCard',
      description: 'Redemption performance insights and revenue analysis',
      allowedRoles: ['admin'] // Only admins can see this
    },
    // CRITICAL FIX: Partner-only navigation items
    {
      label: 'Dashboard',
      path: '/partner-dashboard',
      icon: 'LayoutDashboard',
      description: 'Partner performance dashboard and metrics',
      allowedRoles: ['partner'] // Only partners can see this
    },
    {
      label: 'Offers',
      path: '/partner-offer-management',
      icon: 'Tag',
      description: 'Manage your offers and track performance',
      allowedRoles: ['partner'] // Only partners can see this
    }
    // REMOVED: Create Offer navigation item - no longer available to partners
  ];

  // CRITICAL FIX: Filter navigation items based on user role
  const navigationItems = allNavigationItems.filter(item => 
    item.allowedRoles.includes(userRole)
  );

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const handleItemClick = () => {
    if (onClose) {
      onClose();
    }
  };

  return (
    <aside className={`h-full bg-gray-900 text-white flex flex-col ${isCollapsed ? 'w-16' : 'w-64'}`}>
      {/* Sidebar Header with Logo and Toggle */}
      <div className="p-4 border-b border-gray-700 bg-gray-800/90 h-16 flex items-center justify-between shrink-0">
        {!isCollapsed && (
          <h2 className="text-lg font-bold text-white">
            TravelHeroes
          </h2>
        )}
        
        <Button
          variant="ghost"
          size="icon"
          className="hidden lg:flex text-white hover:bg-gray-700"
          onClick={onToggleCollapse}
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <Icon name={isCollapsed ? "ChevronRight" : "ChevronLeft"} size={16} />
        </Button>

        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden text-white hover:bg-gray-700"
          onClick={onClose}
        >
          <Icon name="X" size={16} />
        </Button>
      </div>

      {/* Navigation Section Header */}
      {!isCollapsed && (
        <div className="p-4 border-b border-gray-700 bg-gray-800/90 shrink-0">
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
            {userRole === 'partner' ? 'Partner Portal' : 'Analytics'}
          </h3>
        </div>
      )}

      {/* Navigation Section - Scrollable */}
      <nav className="flex-1 px-4 py-6 overflow-y-auto">
        <div className="mb-6">
          {!isCollapsed && (
            <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              {userRole === 'partner' ? 'My Account' : 'Partner Management'}
            </h3>
          )}
          <ul className="space-y-1">
            {navigationItems.map((item) => (
              <li key={item.path}>
                <a
                  href={item.path}
                  onClick={handleItemClick}
                  className={`flex items-center ${isCollapsed ? 'justify-center' : 'space-x-3'} px-3 py-2 rounded-lg transition-colors ${
                    isActiveRoute(item.path)
                      ? 'bg-blue-600 text-white' :'text-gray-300 hover:bg-gray-800 hover:text-white'
                  }`}
                  title={isCollapsed ? item.description : undefined}
                >
                  <Icon name={item.icon} size={18} />
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Footer Section */}
      {!isCollapsed && (
        <div className="p-4 border-t border-gray-700 bg-gray-800/90 shrink-0">
          <div className="space-y-2">
            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <Icon name="Database" size={12} className="text-success" />
              <span className="font-medium">Real-time data</span>
            </div>
            <div className="flex items-center space-x-2 text-xs text-gray-400">
              <Icon name="Shield" size={12} className="text-primary" />
              <span className="font-medium">Secure analytics</span>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;